import React, { useState, useEffect } from 'react';
import { MapContainer, TileLayer, Polygon, CircleMarker, Popup, Tooltip } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import {
  getActiveZones,
  getZoneVisitHistory,
  getRiskLevelColor,
  getRiskLevelLabel,
  getZoneCategoryLabel,
  formatDwellTime,
} from '../../../services/highRiskZone.service';

/**
 * ZoneVisitMap - Leaflet map showing high-risk zone polygons and visit markers
 */
const ZoneVisitMap = ({ alienId, days = 30, height = 400 }) => {
  const [zones, setZones] = useState([]);
  const [visits, setVisits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedZone, setSelectedZone] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch zones and visits in parallel
        const [zonesData, historyData] = await Promise.all([
          getActiveZones(),
          alienId ? getZoneVisitHistory(alienId, days) : Promise.resolve(null),
        ]);

        setZones(zonesData || []);
        setVisits(historyData?.visits || []);
      } catch (err) {
        console.error('Error fetching zone map data:', err);
        setError('Failed to load map data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [alienId, days]);

  if (loading) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <h3 className="text-sm font-semibold text-gray-700 mb-3">High-Risk Zone Map</h3>
        <div
          className="bg-gray-100 rounded-lg flex items-center justify-center"
          style={{ height }}
        >
          <div className="text-gray-500">Loading map...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <h3 className="text-sm font-semibold text-gray-700 mb-3">High-Risk Zone Map</h3>
        <div
          className="bg-gray-100 rounded-lg flex items-center justify-center text-red-500"
          style={{ height }}
        >
          {error}
        </div>
      </div>
    );
  }

  // Calculate map center from zones or visits
  const getMapCenter = () => {
    if (visits.length > 0) {
      return [visits[0].latitude, visits[0].longitude];
    }
    if (zones.length > 0 && zones[0].center) {
      return [zones[0].center.latitude, zones[0].center.longitude];
    }
    // Default to US center
    return [39.8283, -98.5795];
  };

  const mapCenter = getMapCenter();

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-gray-700">High-Risk Zone Map</h3>
        <div className="flex items-center gap-4 text-xs">
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 rounded-full bg-orange-400 opacity-50"></div>
            <span className="text-gray-500">Zones ({zones.length})</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 rounded-full bg-red-500"></div>
            <span className="text-gray-500">Visits ({visits.length})</span>
          </div>
        </div>
      </div>

      {/* Map */}
      <div className="rounded-lg overflow-hidden" style={{ height }}>
        <MapContainer
          center={mapCenter}
          zoom={visits.length > 0 ? 12 : 5}
          style={{ height: '100%', width: '100%' }}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />

          {/* Render zone polygons */}
          {zones.map(zone => (
            <ZonePolygon
              key={zone.zoneId}
              zone={zone}
              isSelected={selectedZone === zone.zoneId}
              onSelect={() => setSelectedZone(zone.zoneId)}
            />
          ))}

          {/* Render visit markers */}
          {visits.map((visit, idx) => (
            <VisitMarker key={visit.id || idx} visit={visit} />
          ))}
        </MapContainer>
      </div>

      {/* Legend */}
      <div className="mt-3 pt-3 border-t border-gray-200">
        <h4 className="text-xs font-semibold text-gray-600 mb-2">Risk Levels</h4>
        <div className="flex flex-wrap gap-2">
          {[1, 2, 3, 4, 5].map(level => (
            <div key={level} className="flex items-center gap-1">
              <div
                className="w-3 h-3 rounded"
                style={{ backgroundColor: getRiskLevelColor(level) }}
              />
              <span className="text-xs text-gray-600">{getRiskLevelLabel(level)}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

/**
 * Zone polygon component
 */
const ZonePolygon = ({ zone, isSelected, onSelect }) => {
  if (!zone.boundaryCoordinates || zone.boundaryCoordinates.length < 3) {
    return null;
  }

  // Convert from [lon, lat] to [lat, lon] for Leaflet
  const positions = zone.boundaryCoordinates.map(coord => [coord[1], coord[0]]);
  const color = getRiskLevelColor(zone.riskLevel);

  return (
    <Polygon
      positions={positions}
      pathOptions={{
        color: color,
        fillColor: color,
        fillOpacity: isSelected ? 0.5 : 0.2,
        weight: isSelected ? 3 : 2,
      }}
      eventHandlers={{
        click: onSelect,
      }}
    >
      <Tooltip direction="top" offset={[0, -10]} permanent={false}>
        <div className="text-xs">
          <div className="font-medium">{zone.zoneName}</div>
          <div className="text-gray-500">{getZoneCategoryLabel(zone.zoneCategory)}</div>
          <div style={{ color }}>Risk Level: {zone.riskLevel}</div>
        </div>
      </Tooltip>
      <Popup>
        <div className="p-1">
          <h4 className="font-medium text-sm mb-1">{zone.zoneName}</h4>
          <div className="text-xs text-gray-600 mb-2">
            {getZoneCategoryLabel(zone.zoneCategory)}
          </div>
          <div className="flex items-center gap-1">
            <div
              className="w-4 h-4 rounded flex items-center justify-center text-white text-xs font-medium"
              style={{ backgroundColor: color }}
            >
              {zone.riskLevel}
            </div>
            <span className="text-xs" style={{ color }}>
              {getRiskLevelLabel(zone.riskLevel)} Risk
            </span>
          </div>
          {zone.source && (
            <div className="mt-2 text-xs text-gray-500">
              Source: {zone.source}
            </div>
          )}
        </div>
      </Popup>
    </Polygon>
  );
};

/**
 * Visit marker component
 */
const VisitMarker = ({ visit }) => {
  const riskColor = getRiskLevelColor(visit.riskLevel);
  const entryDate = new Date(visit.entryTime);

  return (
    <CircleMarker
      center={[visit.latitude, visit.longitude]}
      radius={8}
      pathOptions={{
        color: '#fff',
        fillColor: riskColor,
        fillOpacity: 1,
        weight: 2,
      }}
    >
      <Tooltip direction="top" offset={[0, -8]}>
        <div className="text-xs">
          <div className="font-medium">{visit.zoneName}</div>
          <div>{entryDate.toLocaleDateString()}</div>
        </div>
      </Tooltip>
      <Popup>
        <div className="p-1">
          <h4 className="font-medium text-sm mb-1">{visit.zoneName}</h4>
          <div className="text-xs text-gray-600 mb-2">
            {getZoneCategoryLabel(visit.zoneCategory)}
          </div>
          <div className="space-y-1 text-xs">
            <div className="flex items-center gap-1">
              <span className="text-gray-500">Risk:</span>
              <span style={{ color: riskColor }}>{getRiskLevelLabel(visit.riskLevel)}</span>
            </div>
            <div className="flex items-center gap-1">
              <span className="text-gray-500">Entry:</span>
              <span>
                {entryDate.toLocaleDateString()}{' '}
                {entryDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </span>
            </div>
            {visit.dwellSeconds && (
              <div className="flex items-center gap-1">
                <span className="text-gray-500">Dwell:</span>
                <span>{formatDwellTime(visit.dwellSeconds)}</span>
              </div>
            )}
          </div>
        </div>
      </Popup>
    </CircleMarker>
  );
};

export default ZoneVisitMap;
